import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Save, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const MaterialsInventory = () => {
  const { logID } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchInventoryDetails();
  }, [logID]);

  const fetchInventoryDetails = async () => {
    try {
      const res = await axios.get(`/api/material-inventory-logs/${logID}`);
      setDetails(
        res.data.map(detail => ({
          ...detail,
          hasChanged: false,
          currentQuantity: detail.previousQuantity // Initialize current with previous
        }))
      );
    } catch (err) {
      console.error('Error fetching inventory details:', err);
      setError('Failed to load inventory details');
    }
  };

  const handleQuantityChange = (materialID, value) => {
    const numValue = parseFloat(value) || 0;
    setDetails(prev => 
      prev.map(detail => {
        if (detail.materialID === materialID) {
          const difference = numValue - detail.previousQuantity;
          return {
            ...detail,
            currentQuantity: numValue,
            difference,
            hasChanged: true
          };
        }
        return detail;
      })
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedDetails = details
        .filter(detail => detail.hasChanged)
        .map(({ materialID, currentQuantity, previousQuantity }) => ({
          materialID,
          currentQuantity,
          previousQuantity
        }));
  
      console.log('Sending payload:', { details: updatedDetails }); // Log the payload
  
      const response = await axios.post(`/api/material-inventory-logs/${logID}`, {
        details: updatedDetails
      });
  
      console.log('Save response:', response.data); // Log the response
  
      if (response.status === 200) {
        navigate('/material-inventory'); // Ensure this matches your router path
      } else {
        setError('Failed to save inventory changes');
      }
    } catch (err) {
      console.error('Error saving inventory:', err);
      setError('Failed to save inventory changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const filteredDetails = details.filter(detail =>
    detail.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="d-flex flex-column h-100">
      <div className="sticky-top bg-light border-bottom py-2">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0">Materials Inventory Count</h5>
              
              <div className="input-group" style={{ width: '300px' }}>
                <span className="input-group-text">
                  <Search size={18} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search materials..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate('/material-inventory')}
                title="Back to Inventory Logs"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                <Save size={18} className="me-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            />
          </div>
        )}

        <div className="card h-100">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Material</th>
                    <th>Category</th>
                    <th className="text-end">Previous Count</th>
                    <th className="text-end">Current Count</th>
                    <th className="text-end">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.map((detail) => (
                    <tr 
                      key={detail.materialID}
                      className={detail.hasChanged ? 'table-warning' : ''}
                    >
                      <td>{detail.sku}</td>
                      <td>
                        {detail.name}
                        {detail.description && (
                          <>
                            <br />
                            <small className="text-muted">{detail.description}</small>
                          </>
                        )}
                      </td>
                      <td>{detail.categoryName}</td>
                      <td className="text-end">
                        {detail.previousQuantity} {detail.unitOfMeasure}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control text-end"
                          value={detail.currentQuantity || ''}
                          onChange={(e) => handleQuantityChange(detail.materialID, e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className={`text-end ${
                        detail.difference < 0 
                          ? 'text-danger' 
                          : detail.difference > 0 
                            ? 'text-success' 
                            : ''
                      }`}>
                        {detail.difference > 0 ? '+' : ''}
                        {detail.difference} {detail.unitOfMeasure}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsInventory;